import * as React from 'react';
import { BLACK } from '../../styles/colors';
import { Side } from '../position/side';
import TooltipView from './TooltipView';
import { assoc } from '../../language/module';
import { SerializedStyles } from '@emotion/react';


type Props = {
  side: Side;
  arrowColor: string;
  tooltipContent: React.ReactNode;
  children: React.ReactNode;
  activateOnHover: boolean;
  disable: boolean;
  rootCss?: SerializedStyles;
}

type State = {
  isVisible: boolean;
}


/**
 * Tooltip: a component that shows some floating content (props.tooltipContent) relative to an anchor element
 * (props.children). You can choose the side (top/bottom/left/right) the floating content should render on relative to
 * the anchor component. This tooltip can be disabled and triggered by click or hover.
 *
 * If you need more complex logic (e.g. a timer), you'll have to manually manage your own TooltipView.
 *
 * PureComponent: true
 * Connected to Redux: false
 */
export default class Tooltip extends React.PureComponent<Props, State> {
  public static defaultProps = {
    side:            'top',
    arrowColor:      BLACK,
    showTooltip:     false,
    activateOnHover: false,
    disable:         false,
  };

  public state = {
    isVisible: false,
  };


  public onHoverInside = () => {
    if (this.props.disable || !this.props.activateOnHover || this.state.isVisible) {
      return;
    }

    this.open();
  };


  public onHoverOutside = () => {
    if (!this.props.activateOnHover || !this.state.isVisible) {
      return;
    }

    this.close();
  };


  public onClickInside = (event) => {
    if (this.props.disable || this.state.isVisible) {
      this.close();
      return;
    }

    // Tooltips often have to be nested inside other clickable content
    event.preventDefault();

    this.open();
  };


  public onClickOutside = () => this.close();


  public close  = () => this.setState(prev => assoc('isVisible', false, prev));
  public open   = () => this.setState(prev => assoc('isVisible', true, prev));
  public toggle = () => this.setState(prev => assoc('isVisible', !this.state.isVisible, prev));


  public render = () => <TooltipView showTooltip={this.state.isVisible}
                                     side={this.props.side}
                                     arrowColor={this.props.arrowColor}
                                     tooltipContent={this.props.tooltipContent}
                                     children={this.props.children}
                                     activateOnHover={this.props.activateOnHover}
                                     onClickInside={this.onClickInside}
                                     onClickOutside={this.onClickOutside}
                                     onHoverInside={this.onHoverInside}
                                     onHoverOutside={this.onHoverOutside}
                                     rootCss={this.props.rootCss}/>;
}
